

import { useState, useEffect } from "react";
import MainLayouts from "../../../Layout/MainLayouts";
import { ReactComponent as ArrowLeftSquare } from "bootstrap-icons/icons/arrow-left-square-fill.svg";
import Spinner from "../../spinner/Spinner";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import { ReactComponent as Building } from "bootstrap-icons/icons/building.svg";
import { Link, useNavigate, useLocation } from "react-router-dom";
import ApiServices from "../../../api/ApiServices";
import { toast } from "react-toastify";
import { handleError } from "../../../components/utils/AuthenticationCheck";
import CurrencyFormatter from "../../../components/utils/CurrencyFormatter";

import formatDate from "../../../components/utils/DateFormatter";
const BranchDetails = () => {
  const [branchDetails, setBranchDetails] = useState({});
  const [branch_id, setBranchId] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { id } = location.state || {};
  const { branchData } = location.state || {};

  useEffect(() => {
    if (id) setBranchId(id);
  }, []);

  useEffect(() => {
    if (branchData) {
      // Map the data using your structure
      const mappedData = {
        name: branchData.name,
        phone_number: branchData.phone_number,
        is_activated: branchData.is_activated || "NOT AVAILABLE",
        state: branchData.state || "NOT AVAILABLE",
        city: branchData.city || "NOT AVAILABLE",
        status: branchData.status || "NOT AVAILABLE",
        creator: branchData.creator || "NOT AVAILABLE",
        email: branchData.email || "NOT AVAILABLE",
        created_at: branchData.created_at || "NOT AVAILABLE",
        id: branchData.id || "NOT AVAILABLE",
        address: branchData.address,
        sales_representatives: branchData.sales_representatives || [],
        nin: branchData.nin || "NOT AVAILABLE",
        // New columns from the branch details
        wallet: branchData.wallet || "NOT AVAILABLE",
        bank_account: branchData.bank_account || "NOT AVAILABLE",
        wallet_balance: branchData.wallet_balance || "0.00",
        incentive_balance: branchData.incentive_balance || "0.00",
        float_balance: branchData.float_balance || "0.00",
        expense_balance: branchData.expense_balance || "0.00",
        notification_status: branchData.notification_status || false,
        two_factor_enabled: branchData.two_factor_enabled || false,
        head_office: branchData.head_office || null,
        created_at: branchData.created_at,
      };

      setBranchDetails(mappedData);
    }
  }, [branchData]);



  // Safe accessor functions
  const getSafeValue = (value, fallback = "Not Available") => {
    return value || fallback;
  };

  const getSafeArray = (array, fallback = []) => {
    return Array.isArray(array) && array.length > 0 ? array : fallback;
  };

  // Card Component
  const Card = ({ title, children }) => (
    <div className="bg-white shadow-md rounded-xl sm:rounded-2xl p-4 sm:p-5 border border-gray-100">
      <h2 className="text-base sm:text-lg font-semibold text-darkGreen3 mb-3 sm:mb-4 border-b pb-2">
        {title}
      </h2>
      <div className="space-y-2 sm:space-y-2">{children}</div>
    </div>
  );

  // InfoRow Component
  const InfoRow = ({ label, value, badge, highlight }) => (
    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-2 gap-1 sm:gap-0">
      <span className="text-gray-500 font-medium text-sm sm:text-base">{label}</span>
      {badge ? (
        <span
          className={`px-2 py-1 text-xs rounded-full w-fit ${value === "ACTIVE" || value === "Activated"
            ? "bg-darkGreen3 text-lGrey"
            : "bg-dash3 text-lGrey"
            }`}
        >
          {value}
        </span>
      ) : (
        <span
          className={`text-gray-800 text-sm sm:text-base break-words ${highlight ? "font-semibold text-darkGreen3" : ""
            }`}
        >
          {value || "-"}
        </span>
      )}
    </div>
  );

  return (
    <MainLayouts>
      <div className="relative sm:rounded-lg py-4 sm:py-6 w-full px-2 sm:px-0">
        <section className="w-full px-3 sm:pr-[3%] pt-3 my-3 bg-gray-100 rounded-lg">
          <nav className="bg-gray-200 py-2 px-3 sm:px-4 rounded-lg">
            <ol className="list-none p-0 inline-flex flex-wrap mb-2 text-xs sm:text-sm">
              <li className="flex items-center">
                <p className="text-darkGreen3 pr-2">Operations Branch</p>
                <ChevronRightIcon className="w-3 h-3 sm:w-4 sm:h-4" />
              </li>
              <li className="flex items-center">
                <p className="text-orange1">Details</p>
              </li>
            </ol>
          </nav>
        </section>

        {loading ? (
          <Spinner loading={loading} />
        ) : (
          <section className="text-gray-600 body-font">
            <div className="container px-2 sm:px-4">
              <div className="flex justify-end mb-3 px-2 sm:px-0">
                <Link
                  to="/operations/branch/list"
                  className="w-full sm:w-auto text-dGrey3 bg-darkGreen3 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-4 sm:px-5 py-2 sm:py-2.5 text-center"
                >
                  <span className="flex justify-center sm:justify-start items-center mx-1 sm:mx-2">
                    <ArrowLeftSquare className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                    Back
                  </span>
                </Link>
              </div>

              <section className="text-gray-600 body-font px-2 sm:px-4 lg:px-8 pb-6 sm:pb-10">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                  {/* LEFT SIDE */}
                  <div className="lg:col-span-2 space-y-6">
                    {/* Branch Overview */}
                    <Card title="Branch Overview">
                      <InfoRow
                        label="Branch Name"
                        value={branchDetails.name?.toUpperCase()}
                      />
                      <InfoRow label="Email" value={branchDetails.email} />
                      <InfoRow
                        label="Status"
                        value={branchDetails.status?.toUpperCase()}
                        badge
                      />
                      <InfoRow
                        label="Activation"
                        value={
                          branchDetails.is_activated
                            ? "Activated"
                            : "Deactivated"
                        }
                      />
                      <InfoRow
                        label="Created By"
                        value={branchDetails.creator?.fullname}
                      />
                      <InfoRow
                        label="Created Date"
                        value={formatDate(branchDetails.created_at)}
                      />
                    </Card>

                    {/* Location & Contact */}
                    <Card title="Location & Contact">
                      <InfoRow label="State" value={branchDetails.state} />
                      <InfoRow label="City" value={branchDetails.city} />
                      <InfoRow label="Address" value={branchDetails.address} />
                      <InfoRow
                        label="Phone"
                        value={branchDetails.phone_number}
                      />
                      <InfoRow label="NIN" value={branchDetails.nin} />
                    </Card>

                    {/* Branch Management */}
                    <Card title="Branch Management">
                      <InfoRow
                        label="Managers"
                        value={
                          branchDetails.sales_representatives?.length > 0
                            ? branchDetails.sales_representatives.join(", ")
                            : "Not Assigned"
                        }
                      />
                    </Card>
                  </div>

                  {/* RIGHT SIDE */}
                  <div className="lg:col-span-1 space-y-6">
                    <Card title="Wallet Information">

                      <InfoRow
                        label="Wallet"
                        value={CurrencyFormatter(branchDetails.wallet_balance)}
                      />
                      <InfoRow
                        label="Incentive"
                        value={CurrencyFormatter(
                          branchDetails.incentive_balance
                        )}
                      />
                      <InfoRow
                        label="Float"
                        value={CurrencyFormatter(branchDetails.float_balance)}
                      />
                      <InfoRow
                        label="Expense"
                        value={CurrencyFormatter(branchDetails.expense_balance)}
                      />
                      <span>Total Balance :</span>
                      <InfoRow
                        // label="Total Balance"
                        value={CurrencyFormatter(
                          branchDetails.wallet?.total_balance
                        )}
                        highlight
                      />
                    </Card>

                    <Card title="Bank Account">
                      <InfoRow
                        label="Bank Name"
                        value={branchDetails.bank_account?.bank_name}
                      />
                      <InfoRow
                        label="Account Number"
                        value={branchDetails.bank_account?.account_number}
                      />
                      <InfoRow
                        label="Account Name"
                        value={branchDetails.bank_account?.account_name}
                      />
                      <InfoRow
                        label="Bank Code"
                        value={branchDetails.bank_account?.bank_code}
                      />
                    </Card>

                    {/* Head Office Information */}
                    <Card title="Head Office">
                      {branchDetails.head_office ? (
                        <>
                          <InfoRow
                            label="Name"
                            value={branchDetails.head_office.name}
                          />
                          <InfoRow
                            label="Email"
                            value={branchDetails.head_office.email}
                          />
                          <InfoRow
                            label="Phone"
                            value={branchDetails.head_office.phone_number}
                          />
                          <InfoRow
                            label="Location"
                            value={`${branchDetails.head_office.city}, ${branchDetails.head_office.state}`}
                          />
                          <div className="mt-3 sm:mt-4">
                            <Link
                              to="/operations/branch/assign-head-office"
                              state={{ branchData: branchDetails }}
                              className="flex items-center justify-center gap-2 w-full bg-purple-600 text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded text-sm sm:text-base hover:bg-purple-700 transition"
                            >
                              <Building className="w-4 h-4" />
                              <span className="hidden sm:inline">Reassign Head Office</span>
                              <span className="sm:hidden">Reassign HO</span>
                            </Link>
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-3 sm:py-4">
                          <Building className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 text-gray-400" />
                          <p className="text-gray-500 text-xs sm:text-sm mb-3 sm:mb-4">
                            No head office assigned
                          </p>
                          <Link
                            to="/operations/branch/assign-head-office"
                            state={{ branchData: branchDetails }}
                            className="flex items-center justify-center gap-2 w-full bg-orange1 text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded text-sm sm:text-base hover:bg-orange-600 transition"
                          >
                            <Building className="w-4 h-4" />
                            Add Head Office
                          </Link>
                        </div>
                      )}
                    </Card>
                  </div>
                </div>
              </section>
            </div>
          </section>
        )}
      </div>
    </MainLayouts>
  );
};

export default BranchDetails;
